var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { css, keyframes } from '@emotion/react';
import styled from '../../styles/styled';
import { typography, hideVisually } from '../../styles/style-mixins';
import { uniqueId } from '../../util/id';
import { AccessibilityError } from '../../util/errors';
const wrapperStyles = () => css `
  display: flex;
  align-items: center;
`;
const ProgressBarWrapper = styled('div')(wrapperStyles);
const baseStyles = ({ theme }) => css `
  background-color: var(--cui-bg-highlight);
  border-radius: ${theme.borderRadius.pill};
  position: relative;
  width: 100%;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    background-color: var(--cui-bg-accent-strong);
    transition: width 0.05s ease-out;
    height: 100%;
    width: 1px;
  }
`;
const taskStyles = ({ value = 0, max = 1 }) => {
    const width = value && max ? (value / max) * 100 : 0;
    return css `
    &::after {
      width: ${width}%;
    }
  `;
};
const oneAnimation = keyframes `
  0% {
    width: 0%;
  }

  100% {
    width: 100%;
  }
`;
const loopAnimation = keyframes `
  0% {
    width: 0%;
    transform: translateX(0%);
  }

  90% {
    width: 100%;
    transform: translateX(0%);
  }

  100% {
    width: 100%;
    transform: translateX(100%);
  }
`;
const timedStyles = ({ theme, duration = 3000, loop = false, paused = false, }) => css `
  &::after {
    border-radius: ${theme.borderRadius.pill};
    animation-name: ${loop ? loopAnimation : oneAnimation};
    animation-duration: ${duration}ms;
    animation-play-state: ${paused ? 'paused' : 'running'};
    animation-fill-mode: forwards;
    animation-iteration-count: ${loop ? 'infinite' : 1};
  }
`;
const variantStyles = ({ variant = 'primary' }) => {
    const variantMap = {
        primary: 'var(--cui-bg-accent-strong)',
        secondary: 'var(--cui-bg-strong)',
    };
    return css `
    &::after {
      background-color: ${variantMap[variant]};
    }
  `;
};
const sizeStyles = ({ theme, size = 'kilo', }) => {
    const sizeMap = {
        byte: theme.spacings.bit,
        kilo: theme.spacings.byte,
        mega: theme.spacings.mega,
    };
    return css({
        height: sizeMap[size],
    });
};
const TaskProgress = styled('span')(baseStyles, variantStyles, sizeStyles, taskStyles);
const TimeProgress = styled('span')(baseStyles, variantStyles, sizeStyles, timedStyles);
const labelStyles = ({ theme }) => css `
  margin-left: ${theme.spacings.byte};
`;
const labelHiddenStyles = ({ hideLabel }) => hideLabel && hideVisually();
const ProgressBarLabel = styled('span')(typography('two'), labelStyles, labelHiddenStyles);
export function ProgressBar(_a) {
    var { max, value, size, variant, duration, loop, paused, label, hideLabel } = _a, props = __rest(_a, ["max", "value", "size", "variant", "duration", "loop", "paused", "label", "hideLabel"]);
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        !label) {
        throw new AccessibilityError('ProgressBar', 'The `label` prop is missing.');
    }
    const ariaId = uniqueId('progress-bar_');
    return (_jsxs(ProgressBarWrapper, Object.assign({}, props, { children: [max || value ? (_jsx(TaskProgress, { role: "progressbar", "aria-valuenow": value, "aria-valuemin": 0, "aria-valuemax": max, "aria-labelledby": ariaId, max: max, value: value, size: size, variant: variant })) : (_jsx(TimeProgress, { role: "progressbar", "aria-labelledby": ariaId, duration: duration, loop: loop, paused: paused, size: size, variant: variant })), _jsx(ProgressBarLabel, Object.assign({ id: ariaId, hideLabel: hideLabel }, { children: label }))] })));
}
